import { createSelector } from "@reduxjs/toolkit";
import { Card, Conditional, Input, SearchSelect } from "@result/rui/components";
import { ApiFilter, ApiSort } from "@result/rui/utils/api";
import { classNames } from "@result/rui/utils/component";
import PropTypes from "prop-types";
import { useCallback, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { validate } from "uuid";

import DateInput from "#/components/DateInput";
import * as DLD from "#/def/data-layer";
import { SifSpecies } from "#/def/lookup-tables.enums.gen";
import { SifIzvajalec } from "#/def/lookup-tables.ident.gen.js";
import useAuthorization from "#/hooks/useAuthorization.js";
import useAuthorizedBreeder from "#/hooks/useAuthorizedBreeder.js";
import { useDataLayerQuery, useDataLayerRead } from "#/hooks/useDataLayer";
import { usePrefillOperator } from "#/hooks/usePrefillOperator.js";
import * as species from "#/utils/SpeciesScope";

import SelectLookupTableItem from "./SelectLookupTableItem";

/* ---------------- helpers ---------------- */

const safeId = (v) => {
  if (v == null) return null;
  if (typeof v === "object") return v?.id ?? null;
  // allow string/number ids
  return v;
};

export const mergeOptionsWithSelected = (options, selected) => {
  const list = Array.isArray(options) ? options : [];
  if (!selected?.id) return list;

  const selectedId = selected?.id;

  // if selected is already in list, replace it with selected (usually richer)
  let found = false;
  const replaced = list.map((o) => {
    if (o?.id === selectedId) {
      found = true;
      return selected;
    }
    return o;
  });

  // if not found, prepend selected so it’s visible in the Select
  if (!found) return [selected, ...replaced];

  return replaced;
};
export const mergeOptionsWithSelections = ({
  options,
  optionsKey = "id",
  selections,
  selectedKey = "id",
}) => {
  let optionList = Array.isArray(options) ? options : options ? [options] : [];

  let selectedList;
  if (Array.isArray(selections)) {
    selectedList = selections;
  } else {
    selectedList = selections ? [selections] : [];
  }
  if (optionList.length === 0) {
    return selectedList;
  }
  for (const selectedItem of selectedList) {
    const selectedId = selectedItem?.[selectedKey];
    if (selectedId) {
      // if selected is already in list, replace it with selected (usually richer)
      let found = false;
      const replaced = optionList?.map((o) => {
        if (o?.[optionsKey] === selectedId) {
          found = true;
          return selectedItem;
        }
        return o;
      });
      // if not found, prepend selected so it’s visible in the Select
      if (!found) {
        optionList = [selectedItem, ...replaced];
      } else {
        optionList = replaced;
      }
    }
  }

  return optionList;
};

// TODO USE THIS EVERYWHERE INSTEAD OF DIREKTLY mergeOptionsWithSelected
export const makeSelectMergedOptions = ({
  selectContent,
  selectSelected,
  mergeFn = mergeOptionsWithSelected,
}) =>
  createSelector(selectContent, selectSelected, (content, selected) =>
    mergeFn(content, selected)
  );
export const makeKmgMergedSelector = (key, selectedId) =>
  createSelector(
    (state) => state.kmgSubjects?.pageByKey?.[key]?.content || [],
    (state) => (selectedId ? state.kmgSubjects?.byId?.[selectedId] : null),
    (content, selected) => mergeOptionsWithSelected(content, selected)
  );
/* ---------------- component ---------------- */
export const applyBreederSearchFilter = (filter, filterValue) => {
  const accessFilters = [
    [SifSpecies.KONJ, "dostopKonji"],
    [SifSpecies.PRASIC, "dostopPrasici"],
  ];

  const speciesCodeFilter = {
    [SifSpecies.OVCA]: "sifraOvce",
    [SifSpecies.KOZA]: "sifraKoze",
    [SifSpecies.PRASIC]: "sifraPrasici",
    [SifSpecies.KONJ]: "sifraKopitarji",
  };

  const splits = String(filterValue ?? "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);

  filter.isNotNull("id");

  accessFilters.forEach(([speciesType, field]) => {
    if (species.has(speciesType)) {
      filter.isTrue(field);
    }
  });

  if (species.has(SifSpecies.OVCA) && species.has(SifSpecies.KOZA)) {
    filter.or((orGroup) => {
      orGroup.isTrue("dostopOvce");
      orGroup.isTrue("dostopKoze");
    });
  }

  for (const filterString of splits) {
    filter.and((splitFilterAnd) => {
      splitFilterAnd.or((orGroup) => {
        orGroup.likeCaseInsensitive("sifraRejca", filterString);

        if (validate(filterString)) {
          orGroup.equal("id", filterString);
        }

        orGroup.likeCaseInsensitive("email", filterString, true, false);
        orGroup.equal("ime", filterString);
        orGroup.equal("priimek", filterString);
        orGroup.likeCaseInsensitive("imeInPriimek", filterString);

        if (!Number.isNaN(Number(filterString))) {
          orGroup.equal("kmgMid", Number(filterString));
        }

        orGroup.likeCaseInsensitive("naziv", filterString, true, true);
        orGroup.likeCaseInsensitive("naslov.ulica", filterString, false, true);
        orGroup.like("naslov.hisnaStevilka", filterString);
        orGroup.likeCaseInsensitive("naslov.naslovDrugaVrstica", filterString);
        orGroup.like("naslov.postnaStevilka", filterString);

        // eslint-disable-next-line no-shadow
        for (const filterString of splits) {
          // eslint-disable-next-line no-shadow
          filter.and((splitFilterAnd) => {
            // eslint-disable-next-line no-shadow
            splitFilterAnd.or((orGroup) => {
              for (const [speciesType, field] of Object.entries(
                speciesCodeFilter
              )) {
                if (species.has(speciesType)) {
                  orGroup.likeCaseInsensitive(field, filterString);
                }
              }
            });
          });
        }
      });
    });
  }

  return filter;
};

export const displayOption = (option) =>
  "" +
  [
    option?.kmgMid,
    option?.sifraRejca ?? "/",
    species.has(SifSpecies.OVCA) && option?.sifraOvce,
    species.has(SifSpecies.KOZA) && option?.sifraKoze,
    species.has(SifSpecies.PRASIC) && option?.sifraPrasici,
    species.has(SifSpecies.KONJ) && option?.sifraKopitarji,
    option?.naziv || [option.ime, option.priimek].filter(Boolean).join(" "),
  ]
    .filter(Boolean)
    .join(" - ");

const defaultSort = new ApiSort()
  .sort("kmgMid", "asc")
  .sort("naziv", "desc")
  .sort("priimek", "desc")
  .sort("ime", "desc");
const SelectBreederCard = ({
  formField,
  placeholder,
  children,
  showDetails = true,
  renderDetails = null,
  selectLabel = null,
  operatorFormField = null,
  censusDateFormField = null,
  title,
  subTitle,
  onSelection,
  breederDetails: breeder,
  isDisabled = false,
  isRequired = false,
  operatorIsRequired = false,
  censusDateIsRequired = false,
  leftHeaderElement = null,
  childrenInGrid = true,
  isMultiSelect = false,
  onFilter,
  /**
   * NEW:
   * If your form loads only rejecId (and not the full object), pass it here
   * so we can resolve it and include it in options.
   */
  selectedId = null,

  /**
   * NEW:
   * Auto-fetch selected by id if it’s missing in byId.
   */
  autoResolveSelected = true,
  useCard = true,
  entryDateFormField = null,
  entryDateIsRequired = false,
  columns = 3,
  actionButton = null,
  sort = defaultSort,
}) => {
  const { t } = useTranslation();
  const authorizationContext = useAuthorization();
  const { isBreeder, full } = authorizationContext;
  usePrefillOperator({
    formField: operatorFormField,
  });

  const {
    query: subjectQuery,
    isLoading,
    result,
  } = useDataLayerQuery({
    ident: DLD.RKG_SUBJECT,
    mode: "offline",
  });

  const {
    read,
    item,
    isLoading: isLoadingQuery,
  } = useDataLayerRead({
    ident: DLD.RKG_SUBJECT,
    mode: "offline",
  });
  useAuthorizedBreeder({
    callback: (breeder) => {
      formField?.setValue?.(breeder);
      onSelection?.(breeder);
    },
    ignore: () => !!formField?.value || !isBreeder,
    disabled: isDisabled,
  });

  const derivedSelectedId = useMemo(() => {
    // priority: explicit prop > formField.value > breeder prop
    return (
      safeId(selectedId) ?? safeId(formField?.value) ?? safeId(breeder) ?? null
    );
  }, [selectedId, formField?.value, breeder]);

  // we use the “best available” breeder details for display
  const breederDetails = useMemo(() => {
    return (
      (derivedSelectedId != null ? item : null) ??
      breeder ??
      formField?.value ??
      null
    );
  }, [item, breeder, formField?.value, derivedSelectedId]);

  // prevent repeated fetch loops for the same id
  useEffect(() => {
    if (!autoResolveSelected) return;
    if (derivedSelectedId == null) return;

    read(derivedSelectedId);
  }, [autoResolveSelected, derivedSelectedId]);

  // combine page options + selected option (resolved by id) so Select can display it
  const combinedOptions = useMemo(() => {
    return mergeOptionsWithSelected(result.content, formField?.value);
  }, [result, formField?.value]);

  // stable selectors passed into SearchSelect (it calls useSelector(getOptionsState))
  const getOptionsState = useCallback(() => combinedOptions, [combinedOptions]);
  const getLoadingState = useCallback(
    (state) => state.kmgSubjects.isLoading,
    []
  );
  const getPageNumberState = useCallback(
    (state) => state.kmgSubjects.page?.page,
    []
  );

  const innerContent = (
    <>
      <div
        className={classNames(
          `fixed-grid has-1-cols-mobile has-${columns}-cols`,
          {
            "has-6-cols-widescreen ":
              (!columns || columns > 6) &&
              showDetails &&
              breederDetails != null,
          }
        )}>
        <div className="grid is-gap-2">
          <Conditional condition={!!isDisabled}>
            <Input
              value={[
                breederDetails?.kmgMid,
                breederDetails?.sifraRejca ?? "/",
                breederDetails?.naziv ||
                  [breederDetails?.ime, breederDetails?.priimek]
                    .filter(Boolean)
                    .join(" "),
              ]
                .filter(Boolean)
                .join(" - ")}
              isDisabled={true}
              isRequired={isRequired}
              label={selectLabel ?? t("form.breederCode")}
              placeholder={placeholder}
            />
          </Conditional>
          <Conditional condition={!isDisabled}>
            <div
              className={`${
                actionButton ? "is-flex is-align-items-flex-end is-gap-2" : ""
              } select-overlay-wrap`}>
              <div
                className={actionButton ? "is-flex-grow-1" : undefined}
                style={actionButton ? { minWidth: 0 } : undefined}>
                <SearchSelect
                  name={"trenutniRejec"}
                  label={selectLabel ?? t("form.breederCode")}
                  placeholder={placeholder}
                  formField={formField}
                  isRequired={isRequired}
                  isDisabled={isDisabled || authorizationContext?.isBreeder}
                  optionId="id"
                  optionLabel={displayOption}
                  pageSize={10}
                  sort={sort ?? ApiSort.sort("createdOn")}
                  onSelection={onSelection}
                  getLoadingState={getLoadingState}
                  getOptionsState={getOptionsState}
                  getPageNumberState={getPageNumberState}
                  onFetchData={(page, query, sort) => {
                    const filter = new ApiFilter();
                    applyBreederSearchFilter(filter, query);
                    subjectQuery({ page, filter, sort });
                  }}
                />
              </div>
              {actionButton}
            </div>
          </Conditional>
          <Conditional condition={showDetails && breederDetails != null}>
            {typeof renderDetails === "function" ? (
              renderDetails({ breederDetails, t })
            ) : (
              <>
                <Input
                  name="kmgName"
                  label={t("form.breederTitle")}
                  value={breederDetails?.naziv}
                  isDisabled={true}
                />
                <Input
                  name="kmgAdress"
                  label={t("form.breederAddress")}
                  value={[
                    breederDetails?.naslov?.ulica,
                    breederDetails?.naslov?.hisnaStevilka,
                  ]
                    .filter(Boolean)
                    .join(" ")}
                  isDisabled={true}
                />
                <Input
                  name="kmgPost"
                  label={t("kmgEntity.kmgPost")}
                  value={[
                    breederDetails?.naslov?.postnaStevilka,
                    breederDetails?.naslov?.posta,
                  ]
                    .filter(Boolean)
                    .join(" ")}
                  isDisabled={true}
                />
                <Input
                  name="kmgPhone"
                  label={t("form.code")}
                  value={breederDetails?.sifraRejca}
                  isDisabled={true}
                />
                <Input
                  className={"span-2-cols"}
                  name="institutionOrKgz"
                  label={t("kmgEntity.institution")}
                  value={breederDetails?.odgovorniZavod ?? "/"}
                  isDisabled={true}
                />
              </>
            )}
          </Conditional>
          <Conditional condition={!!operatorFormField}>
            <SelectLookupTableItem
              label={t("form.operator")}
              ident={SifIzvajalec}
              isRequired={operatorIsRequired}
              formField={
                operatorFormField || { value: null, setValue: () => {} }
              }
              isDisabled={isDisabled}
            />
          </Conditional>
          <Conditional condition={!!censusDateFormField}>
            <DateInput
              label={t("form.censusDate")}
              isRequired={
                censusDateIsRequired ?? censusDateFormField?.isRequired
              }
              formField={censusDateFormField}
              isDisabled={isDisabled && censusDateFormField?.value != null}
            />
          </Conditional>
          <Conditional condition={!!entryDateFormField}>
            <DateInput
              label={t("form.entryDate")}
              isRequired={entryDateIsRequired}
              formField={entryDateFormField}
              isDisabled={isDisabled}
            />
          </Conditional>
          <Conditional condition={childrenInGrid}>{children}</Conditional>
        </div>
      </div>
      <Conditional condition={!childrenInGrid}>{children}</Conditional>
    </>
  );

  return useCard ? (
    <Card
      title={title ?? t("form.breederDetails")}
      subTitle={subTitle ?? t("form.breederData")}
      leftHeaderElement={leftHeaderElement}>
      {innerContent}
    </Card>
  ) : (
    <>{innerContent}</>
  );
};

export default SelectBreederCard;

SelectBreederCard.propTypes = {
  children: PropTypes.node,
  placeholder: PropTypes.string,
  formField: PropTypes.object,
  showDetails: PropTypes.bool,
  renderDetails: PropTypes.func,
  selectLabel: PropTypes.string,
  title: PropTypes.string,
  subTitle: PropTypes.string,
  onSelection: PropTypes.func,
  breederDetails: PropTypes.object,
  isDisabled: PropTypes.bool,

  // NEW
  selectedId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  autoResolveSelected: PropTypes.bool,
  actionButton: PropTypes.node,
};
